import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Badge } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"

const CCvsVLCCReport = () => {
  const [loading, setLoading] = useState(true)
  const [vlcc, setVlcc] = useState(null)
  const [cc, setCc] = useState(null)
  const [trend, setTrend] = useState([])
  const [totalQty, setTotalQty] = useState(0)
  const [ccBreakdown, setCcBreakdown] = useState([])
  const [expandedCC, setExpandedCC] = useState({})

  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))

  useEffect(() => { loadData() }, [from, to])

  const loadData = async () => {
    setLoading(true)
    try {
      const resp = await api.get("reports/cc-vs-vlcc", { params: { from, to } })
      if (resp.data.success) {
        const d = resp.data.data
        setVlcc(d.vlcc || null)
        setCc(d.cc || null)
        setTrend(d.trend || [])
        setTotalQty(d.totalQty || 0)
        setCcBreakdown(d.ccBreakdown || [])
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const maxTrend = Math.max(...trend.map(t => Math.max(t.vlccQty, t.ccQty)), 1)
  const v = (val) => Number(val || 0)
  const vlccPct = totalQty > 0 ? Math.round((v(vlcc?.totalQty) / totalQty) * 100) : 50
  const ccPct = 100 - vlccPct

  const toggleCC = (ccId) => {
    setExpandedCC(prev => ({ ...prev, [ccId]: !prev[ccId] }))
  }

  const ccColors = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"]

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={5}><h4><i className="fas fa-exchange-alt me-2" style={{ color: "#6366f1" }}></i>CC vs VLCC Comparison</h4></Col>
          <Col md={7} style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={from} onChange={e => setFrom(e.target.value)} />
            <span style={{ color: "#6c757d" }}>to</span>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={to} onChange={e => setTo(e.target.value)} />
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Distribution Bar */}
            <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "16px" }}>Procurement Distribution</h6>
              <div style={{ height: "40px", borderRadius: "8px", overflow: "hidden", display: "flex" }}>
                <div style={{ width: `${vlccPct}%`, background: "linear-gradient(90deg, #6366f1, #818cf8)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "700", fontSize: "13px", transition: "width 0.5s" }}>
                  🥛 VLCC {vlccPct}%
                </div>
                <div style={{ width: `${ccPct}%`, background: "linear-gradient(90deg, #0ea5e9, #38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: "700", fontSize: "13px", transition: "width 0.5s" }}>
                  🏭 CC {ccPct}%
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontSize: "12px", color: "#6c757d" }}>
                <span>{v(vlcc?.totalQty).toLocaleString("en-IN")} Ltrs</span>
                <span style={{ fontWeight: "700", color: "#1e293b" }}>Total: {v(totalQty).toLocaleString("en-IN")} Ltrs</span>
                <span>{v(cc?.totalQty).toLocaleString("en-IN")} Ltrs</span>
              </div>
            </div>

            {/* Side-by-side Summary */}
            <Row className="mb-4">
              <Col md={6}>
                <div style={{ background: "linear-gradient(135deg, #6366f115, #6366f108)", border: "2px solid #c7d2fe", borderRadius: "12px", padding: "24px" }}>
                  <h5 style={{ fontWeight: "700", color: "#6366f1", marginBottom: "16px" }}>🥛 VLCC Purchases (All Centers)</h5>
                  {[
                    { label: "Total Quantity", value: v(vlcc?.totalQty).toLocaleString("en-IN") + " Ltrs" },
                    { label: "Total Entries", value: v(vlcc?.entries).toLocaleString("en-IN") },
                    { label: "Active Producers", value: v(vlcc?.suppliers) },
                    { label: "Average FAT", value: v(vlcc?.avgFat).toFixed(2) },
                    { label: "Average SNF", value: v(vlcc?.avgSnf).toFixed(2) },
                    { label: "Average Rate", value: "₹" + v(vlcc?.avgRate).toFixed(2) },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 5 ? "1px solid #e0e7ff" : "none" }}>
                      <span style={{ color: "#64748b", fontSize: "13px" }}>{item.label}</span>
                      <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px" }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </Col>
              <Col md={6}>
                <div style={{ background: "linear-gradient(135deg, #0ea5e915, #0ea5e908)", border: "2px solid #bae6fd", borderRadius: "12px", padding: "24px" }}>
                  <h5 style={{ fontWeight: "700", color: "#0ea5e9", marginBottom: "16px" }}>🏭 CC Purchases (Bulk Vendors)</h5>
                  {[
                    { label: "Total Quantity", value: v(cc?.totalQty).toLocaleString("en-IN") + " Ltrs" },
                    { label: "Total Entries", value: v(cc?.entries).toLocaleString("en-IN") },
                    { label: "Active Vendors", value: v(cc?.suppliers) },
                    { label: "Average FAT", value: v(cc?.avgFat).toFixed(2) },
                    { label: "Average SNF", value: v(cc?.avgSnf).toFixed(2) },
                    { label: "Average Rate", value: "₹" + v(cc?.avgRate).toFixed(2) },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 5 ? "1px solid #bae6fd" : "none" }}>
                      <span style={{ color: "#64748b", fontSize: "13px" }}>{item.label}</span>
                      <span style={{ fontWeight: "700", color: "#1e293b", fontSize: "14px" }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            {/* ═══════════ PER-CC BREAKDOWN (Accordion) ═══════════ */}
            <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "16px" }}>
                <i className="fas fa-sitemap me-2" style={{ color: "#6366f1" }}></i>Per-CC Breakdown — Which VLCCs belong to which CC
              </h6>

              {ccBreakdown.length === 0 ? (
                <div className="text-center py-4 text-muted">No chilling centers found</div>
              ) : ccBreakdown.map((ccItem, idx) => {
                const color = ccColors[idx % ccColors.length]
                const isOpen = expandedCC[ccItem.cc_id]
                return (
                  <div key={ccItem.cc_id} style={{
                    border: `2px solid ${isOpen ? color : '#e2e8f0'}`, borderRadius: "12px",
                    marginBottom: "12px", overflow: "hidden", transition: "border-color 0.3s"
                  }}>
                    {/* CC Header — clickable */}
                    <div onClick={() => toggleCC(ccItem.cc_id)} style={{
                      padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center",
                      justifyContent: "space-between", background: isOpen ? `${color}08` : "#fff",
                      transition: "background 0.3s"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: "10px", display: "flex", alignItems: "center",
                          justifyContent: "center", background: `${color}15`, color: color, fontWeight: 700, fontSize: "16px"
                        }}>
                          <i className="fas fa-warehouse"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>
                            {ccItem.cc_name}
                            <Badge color="primary" style={{ marginLeft: "8px", fontSize: "11px" }}>{ccItem.vlcc_count} VLCCs</Badge>
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b" }}>
                            Total Intake: <b style={{ color }}>{v(ccItem.cc_total_intake).toLocaleString("en-IN")} Ltrs</b>
                            {" • "}VLCC: <b>{v(ccItem.vlcc_total_qty).toLocaleString("en-IN")}</b>
                            {ccItem.cc_bulk_qty > 0 && <>{" • "}Bulk: <b>{v(ccItem.cc_bulk_qty).toLocaleString("en-IN")}</b></>}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        {/* Mini stat pills */}
                        <div style={{ display: "flex", gap: "8px" }}>
                          {ccItem.cc_avg_fat > 0 && <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 600 }}>FAT {ccItem.cc_avg_fat}</span>}
                          {ccItem.cc_avg_snf > 0 && <span style={{ background: "#dbeafe", color: "#1e40af", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 600 }}>SNF {ccItem.cc_avg_snf}</span>}
                        </div>
                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: "#94a3b8", fontSize: "14px" }}></i>
                      </div>
                    </div>

                    {/* Expanded: VLCC Table */}
                    {isOpen && (
                      <div style={{ padding: "0 20px 16px", background: "#fafbfc" }}>
                        {ccItem.vlccs.length === 0 ? (
                          <div className="text-center py-3 text-muted" style={{ fontSize: "13px" }}>
                            <i className="fas fa-info-circle me-1"></i>No VLCCs assigned to this CC
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table mb-0" style={{ fontSize: "12px" }}>
                              <thead>
                                <tr style={{ background: "#f1f5f9", fontSize: "10px", color: "#64748b", textTransform: "uppercase" }}>
                                  <th style={{ borderLeft: `3px solid ${color}` }}>VLCC Name</th>
                                  <th>#</th>
                                  <th>Qty (Ltrs)</th>
                                  <th>% of CC</th>
                                  <th>Entries</th>
                                  <th>Producers</th>
                                  <th>Avg FAT</th>
                                  <th>Avg SNF</th>
                                  <th>Avg Rate</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ccItem.vlccs.map(vlccItem => {
                                  const pct = ccItem.cc_total_intake > 0
                                    ? Math.round((vlccItem.totalQty / ccItem.cc_total_intake) * 100)
                                    : 0
                                  return (
                                    <tr key={vlccItem.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                      <td style={{ fontWeight: 600, color: "#1e293b", borderLeft: `3px solid ${color}` }}>
                                        <Link to={"/vlcc-mobile-login/" + vlccItem.id} style={{ color: "#1e293b", textDecoration: "none" }}>
                                          <i className="fas fa-store-alt me-1" style={{ color, fontSize: "11px" }}></i>
                                          {vlccItem.name}
                                        </Link>
                                      </td>
                                      <td style={{ color: "#94a3b8" }}>{vlccItem.number}</td>
                                      <td style={{ fontWeight: 700, color }}>{v(vlccItem.totalQty).toLocaleString("en-IN")}</td>
                                      <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                          <div style={{ width: "50px", height: "6px", background: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                                            <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: "3px" }}></div>
                                          </div>
                                          <span style={{ fontSize: "11px", fontWeight: 600 }}>{pct}%</span>
                                        </div>
                                      </td>
                                      <td>{vlccItem.entries}</td>
                                      <td>{vlccItem.producers}</td>
                                      <td style={{ fontWeight: 600 }}>{v(vlccItem.avgFat).toFixed(2)}</td>
                                      <td style={{ fontWeight: 600 }}>{v(vlccItem.avgSnf).toFixed(2)}</td>
                                      <td style={{ fontWeight: 600 }}>₹{v(vlccItem.avgRate).toFixed(2)}</td>
                                    </tr>
                                  )
                                })}
                                {/* Totals row */}
                                <tr style={{ background: `${color}08`, fontWeight: 700 }}>
                                  <td style={{ borderLeft: `3px solid ${color}`, color }}>
                                    <i className="fas fa-sigma me-1"></i>Total ({ccItem.vlccs.length} VLCCs)
                                  </td>
                                  <td></td>
                                  <td style={{ color }}>{v(ccItem.vlcc_total_qty).toLocaleString("en-IN")}</td>
                                  <td>
                                    {ccItem.cc_total_intake > 0 &&
                                      <span style={{ fontSize: "11px" }}>{Math.round((ccItem.vlcc_total_qty / ccItem.cc_total_intake) * 100)}%</span>
                                    }
                                  </td>
                                  <td>{ccItem.vlccs.reduce((s, v) => s + v.entries, 0)}</td>
                                  <td>{ccItem.vlccs.reduce((s, v) => s + v.producers, 0)}</td>
                                  <td colSpan={3}></td>
                                </tr>
                                {/* CC Bulk row if exists */}
                                {ccItem.cc_bulk_qty > 0 && (
                                  <tr style={{ background: "#eff6ff", fontWeight: 600 }}>
                                    <td style={{ borderLeft: "3px solid #0ea5e9", color: "#0ea5e9" }}>
                                      <i className="fas fa-truck me-1"></i>Bulk Vendor Purchases
                                    </td>
                                    <td></td>
                                    <td style={{ color: "#0ea5e9" }}>{v(ccItem.cc_bulk_qty).toLocaleString("en-IN")}</td>
                                    <td>
                                      <span style={{ fontSize: "11px" }}>{Math.round((ccItem.cc_bulk_qty / ccItem.cc_total_intake) * 100)}%</span>
                                    </td>
                                    <td>{ccItem.cc_entries}</td>
                                    <td>{ccItem.cc_vendors} vendors</td>
                                    <td>{ccItem.cc_avg_fat > 0 ? ccItem.cc_avg_fat : "—"}</td>
                                    <td>{ccItem.cc_avg_snf > 0 ? ccItem.cc_avg_snf : "—"}</td>
                                    <td></td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Trend Comparison Chart */}
            {trend.length > 0 && (
              <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "20px" }}><i className="fas fa-chart-bar me-2"></i>Monthly Volume Comparison</h6>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", height: "180px", padding: "0 10px" }}>
                  {trend.map((t, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: "3px", alignItems: "flex-end", height: "140px" }}>
                        <div style={{
                          width: "22px", height: Math.max(4, (t.vlccQty / maxTrend) * 130) + "px",
                          background: "linear-gradient(180deg, #6366f1, #818cf8)",
                          borderRadius: "4px 4px 0 0", transition: "height 0.5s",
                        }} title={`VLCC: ${v(t.vlccQty).toLocaleString("en-IN")} Ltrs`}></div>
                        <div style={{
                          width: "22px", height: Math.max(4, (t.ccQty / maxTrend) * 130) + "px",
                          background: "linear-gradient(180deg, #0ea5e9, #38bdf8)",
                          borderRadius: "4px 4px 0 0", transition: "height 0.5s",
                        }} title={`CC: ${v(t.ccQty).toLocaleString("en-IN")} Ltrs`}></div>
                      </div>
                      <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "8px", fontWeight: "500" }}>{t.monthShort}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: "24px", justifyContent: "center", marginTop: "12px", fontSize: "12px" }}>
                  <span><span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "3px", background: "#6366f1", marginRight: "6px" }}></span>VLCC</span>
                  <span><span style={{ display: "inline-block", width: "12px", height: "12px", borderRadius: "3px", background: "#0ea5e9", marginRight: "6px" }}></span>CC</span>
                </div>
              </div>
            )}

            {/* Monthly Data Table */}
            <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <div className="table-responsive">
                <table className="table mb-0" style={{ fontSize: "12px" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", fontSize: "10px", color: "#64748b", textTransform: "uppercase" }}>
                      <th>Month</th><th>VLCC Qty</th><th>CC Qty</th><th>Total</th><th>VLCC %</th><th>CC %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trend.map((t, i) => {
                      const total = v(t.vlccQty) + v(t.ccQty)
                      const vp = total > 0 ? Math.round((v(t.vlccQty) / total) * 100) : 0
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ fontWeight: "600" }}>{t.month}</td>
                          <td style={{ color: "#6366f1", fontWeight: "600" }}>{v(t.vlccQty).toLocaleString("en-IN")}</td>
                          <td style={{ color: "#0ea5e9", fontWeight: "600" }}>{v(t.ccQty).toLocaleString("en-IN")}</td>
                          <td style={{ fontWeight: "700" }}>{v(total).toLocaleString("en-IN")}</td>
                          <td>{vp}%</td>
                          <td>{100 - vp}%</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CCvsVLCCReport
